/**
 * Single HTTP client every backend call goes through — auth headers and
 * error normalization live here once (per repo convention), not scattered
 * across feature services.
 *
 * Response envelope (every POST route except the API Gateway mock `/health`):
 *   success → { status: 'success', data, message, timestamp, requestId }
 *   error   → { status: 'error', errorCode, message, details, ... }
 */

// TEMPORARY: hardcoded to the dev-stage deployment. Re-derive with:
//   aws cloudformation describe-stacks --stack-name WsPhilanthropyCompute \
//     --query 'Stacks[0].Outputs[?OutputKey==`ApiUrl`].OutputValue' \
//     --output text --region us-east-1
// TODO(infra): swap for a per-environment build config once the CDK output
// is wired into the frontend build/deploy pipeline.
const API_BASE_URL = 'https://kinqf38v21.execute-api.us-east-1.amazonaws.com/dev';

interface ApiEnvelopeSuccess<T> {
  status: 'success';
  message: string;
  data: T;
}

interface ApiEnvelopeError {
  status: 'error';
  message: string;
  errorCode: string;
  details?: unknown;
}

export class ApiError extends Error {
  errorCode: string;
  details?: unknown;

  constructor(message: string, errorCode: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.errorCode = errorCode;
    this.details = details;
  }
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init.headers },
  });
  const json = (await res.json()) as ApiEnvelopeSuccess<T> | ApiEnvelopeError;
  if (json.status === 'error') {
    throw new ApiError(json.message, json.errorCode, json.details);
  }
  return json.data;
}

export function postJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}
